import { Box, Button, Divider, Typography } from '@mui/material';
import { useMemo } from 'react';
import { generatePath, Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { Social_Tag_Type_Enum } from '@kheops/graphql';
import { RoutePaths } from '../../routes/AppRoutes';
import FileUtils from '../../common/utils/file.utils';
import { getMinimumOrderValueFromDeliveryZones } from '../../common/utils/common.utils';
import { linkStyle } from '../../common/utils/style.utils';
import { ContractPreview } from '../../hooks/useContracts';
import ContactBox from '../../company/companyInfo/ContactBox';
import useRelevantSocialTags from '../../social-proof/hooks/useRelevantSocialTags';
import { currentContextAtom } from '../../state';
import useCurrentBreakpoint from '../../hooks/useCurrentBreakpoint';
import CompanySocialTagsSubtitle from '../../social-proof/CompanySocialTagsSubtitle';
import { Product } from './PackagingPage';

export interface PackagingPageHeaderProps {
  product: Product;
  contract?: ContractPreview;
}

export default function PackagingPageHeader({ product, contract }: PackagingPageHeaderProps): React.JSX.Element {
  const breakpoint = useCurrentBreakpoint();
  const { t } = useTranslation(['common', 'packaging', 'social-proof']);
  const navigate = useNavigate();
  const { companyAddress } = useAtomValue(currentContextAtom);
  const supplierLink = generatePath(RoutePaths.CONTRACT_COMPANY, { companyId: product.company_id });
  const companySocialTags = useRelevantSocialTags(
    product.company.social_tags,
    {
      rules: { 1: 1, 2: 1 },
      excluded: [Social_Tag_Type_Enum.HighMargin, Social_Tag_Type_Enum.GuaranteedSales],
    },
  );

  const minimumOrderValue = getMinimumOrderValueFromDeliveryZones(product.company.delivery_zones, companyAddress.administrativeAreaLevel2!);

  const contactBoxActionDisplay = useMemo(() => {
    switch (breakpoint) {
      case 'xs':
      case 'sm':
        return 'icon';
      case 'md':
        return 'short';
      case 'lg':
      case 'xl':
        return 'text';
    }
  }, [breakpoint]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box
          component={Link}
          to={supplierLink}
          sx={{ flex: 1, ...linkStyle }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              component="img"
              src={FileUtils.BuildCompanyImageUrl({ photo: product.company.photos[0]?.photo, type: 'SUPPLIER', size: { height: 48, width: 48 } })}
              sx={{ mr: 1, height: 48, width: 48, borderRadius: '100%' }}
            />
            <Box>
              <Typography variant="titleMedium" sx={{ textDecoration: 'underline' }}>{product.company.tradeName}</Typography>
              <Typography variant="bodySmall">
                <CompanySocialTagsSubtitle
                  isWholesaler={product.company.is_wholesaler}
                  tags={companySocialTags}
                  geoloc={{
                    lat: product.company.addresses[0].latitude,
                    lng: product.company.addresses[0].longitude,
                  }}
                  minimumOrderValue={minimumOrderValue}
                />
              </Typography>
            </Box>
          </Box>
        </Box>
        {breakpoint !== 'xs' && breakpoint !== 'sm' && (
          <Button variant="outlined" onClick={() => navigate(supplierLink)}>
            {t('common:see_profile')}
          </Button>
        )}
        <ContactBox
          contactCompanyId={product.company.id}
          contract={contract}
          buttonSize="medium"
          actionButtonDisplay={contactBoxActionDisplay}
        />
      </Box>
      <Divider sx={{ my: 2 }} />
    </>
  );
}
